import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AdminStatusBadge,
  AdminSupportingText,
  adminFormTwoColGridClass,
} from '../shared/adminUx';
import {
  AUTH_PROVIDER_INFO,
  AUTH_REQUIREMENTS,
  PASSWORD_POLICY_DISPLAY,
  SESSION_DEFAULTS,
  SESSION_RULE_REFERENCES,
} from './settingsDefaults';
import {
  SettingsConfirmModal,
  SettingsField,
  SettingsReadOnlyRow,
  SettingsRelatedLink,
  SettingsSectionShell,
  SettingsSuccessToast,
  useSettingsPanelState,
} from './SettingsSectionShell';

const SUBSECTIONS = [
  { id: 'authentication', label: 'Authentication' },
  { id: 'session', label: 'Session Controls' },
  { id: 'password', label: 'Password Policy' },
  { id: 'related', label: 'Related Security Controls' },
];

const SecurityCompliancePanel = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('authentication');
  const [confirm, setConfirm] = useState(null);

  const session = useSettingsPanelState({ ...SESSION_DEFAULTS });

  const panels = {
    authentication: { hasChanges: false, saving: false, cancel: () => {}, successMessage: '' },
    session,
    password: { hasChanges: false, saving: false, cancel: () => {}, successMessage: '' },
    related: { hasChanges: false, saving: false, cancel: () => {}, successMessage: '' },
  };
  const activePanel = panels[activeId];
  const readOnlyPanel = activeId !== 'session';

  const requestSave = () => {
    if (activeId !== 'session') return;
    setConfirm({
      title: 'Change Session Policy?',
      message: 'This changes the session policy used by the applicable account flow.',
    });
  };

  const performSave = async () => {
    setConfirm(null);
    await session.save(() => ({}), 'Session controls updated.');
  };

  const body = {
    authentication: (
      <>
        <AdminSupportingText className="font-medium text-offblack">
          Provider information
        </AdminSupportingText>
        <div className={adminFormTwoColGridClass}>
          <SettingsReadOnlyRow
            label="Authentication Provider"
            value={AUTH_PROVIDER_INFO.authenticationProvider}
          />
          <div className="space-y-2 w-full min-w-0">
            <SettingsReadOnlyRow label="Provider Status" value={AUTH_PROVIDER_INFO.providerStatus} />
            <AdminStatusBadge status="Warning" label="Warning" />
            <AdminSupportingText>{AUTH_PROVIDER_INFO.note}</AdminSupportingText>
          </div>
          <SettingsReadOnlyRow label="Admin Access Gate" value={AUTH_PROVIDER_INFO.adminAccessGate} />
          <SettingsReadOnlyRow label="Last Provider Check" value={AUTH_PROVIDER_INFO.lastProviderCheck} />
        </div>
        <AdminSupportingText className="font-medium text-offblack">
          Authentication requirements
        </AdminSupportingText>
        <div className={adminFormTwoColGridClass}>
          <SettingsReadOnlyRow label="Platform Owner MFA" value={AUTH_REQUIREMENTS.platformOwnerMfa} />
          <SettingsReadOnlyRow label="Co-owner MFA" value={AUTH_REQUIREMENTS.coOwnerMfa} />
          <SettingsReadOnlyRow label="Team Member MFA" value={AUTH_REQUIREMENTS.teamMemberMfa} />
          <SettingsReadOnlyRow
            label="Customer verified mobile"
            value={AUTH_REQUIREMENTS.customerVerifiedMobile}
          />
          <SettingsReadOnlyRow
            label="Business verified mobile"
            value={AUTH_REQUIREMENTS.businessVerifiedMobile}
          />
          <SettingsReadOnlyRow
            label="Business sensitive-action MFA"
            value={AUTH_REQUIREMENTS.businessSensitiveActionMfa}
          />
          <SettingsReadOnlyRow
            label="Customer sensitive-action re-verification"
            value={AUTH_REQUIREMENTS.customerSensitiveReVerification}
          />
          <SettingsReadOnlyRow
            label="Routine SMS challenge at every normal login"
            value={AUTH_REQUIREMENTS.routineSmsChallenge}
          />
          <SettingsReadOnlyRow
            label="Force logout after confirmed password reset"
            value={AUTH_REQUIREMENTS.forceLogoutAfterPasswordReset}
          />
        </div>
      </>
    ),
    session: (
      <>
        <div className={adminFormTwoColGridClass}>
          {SESSION_RULE_REFERENCES.map((row) => (
            <div key={row.label} className="space-y-1 w-full min-w-0">
              <SettingsReadOnlyRow label={row.label} value={row.value} />
              <SettingsRelatedLink onClick={() => navigate('/admin/configuration/limits')}>
                Open Limits & Thresholds
              </SettingsRelatedLink>
            </div>
          ))}
        </div>
        <SettingsField label="Invalidate Sessions After Password Change">
          <label className="flex items-center gap-2 text-sm text-offblack">
            <input
              type="checkbox"
              checked={session.draft.invalidateSessionsAfterPasswordChange}
              onChange={(e) =>
                session.setField('invalidateSessionsAfterPasswordChange', e.target.checked)
              }
              className="w-4 h-4 text-primaryblue border-lightgray rounded"
            />
            On
          </label>
        </SettingsField>
        <SettingsReadOnlyRow
          label="Invalidate Sessions After Password Reset"
          value="On — locked"
        />
        <div className={adminFormTwoColGridClass}>
          <SettingsReadOnlyRow label="Public and Admin Session Separation" value="Enforced" />
          <SettingsReadOnlyRow label="Admin Host-Only Cookies" value="Enforced" />
        </div>
      </>
    ),
    password: (
      <>
        <div className={adminFormTwoColGridClass}>
          <div className="space-y-1 w-full min-w-0">
            <SettingsReadOnlyRow
              label="Minimum Password Length"
              value={PASSWORD_POLICY_DISPLAY.minimumPasswordLength}
            />
            <SettingsRelatedLink onClick={() => navigate('/admin/configuration/validation')}>
              Open Validation
            </SettingsRelatedLink>
          </div>
          <SettingsReadOnlyRow
            label="Required Character Types"
            value={PASSWORD_POLICY_DISPLAY.requiredCharacterTypes}
          />
          <div className="space-y-1 w-full min-w-0">
            <SettingsReadOnlyRow
              label="Password Reset Link Expiry"
              value={PASSWORD_POLICY_DISPLAY.passwordResetLinkExpiry}
            />
            <SettingsRelatedLink onClick={() => navigate('/admin/configuration/limits')}>
              Open Limits & Thresholds
            </SettingsRelatedLink>
          </div>
          <div className="space-y-1 w-full min-w-0">
            <SettingsReadOnlyRow
              label="Team Temporary Password Expiry"
              value={PASSWORD_POLICY_DISPLAY.teamTemporaryPasswordExpiry}
            />
            <SettingsRelatedLink onClick={() => navigate('/admin/configuration/limits')}>
              Open Limits & Thresholds
            </SettingsRelatedLink>
          </div>
          <SettingsReadOnlyRow
            label="Failed Sign-In Protection"
            value={PASSWORD_POLICY_DISPLAY.failedSignInProtection}
          />
          <SettingsReadOnlyRow
            label="Password Policy Provider Support"
            value={PASSWORD_POLICY_DISPLAY.passwordPolicyProviderSupport}
          />
        </div>
      </>
    ),
    related: (
      <>
        <div className="space-y-2">
          <SettingsRelatedLink
            onClick={() => navigate('/admin/integrations')}
          >
            Open Authentication Integration
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/integrations')}>
            Open Verification Integration
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/configuration/validation')}>
            Open Validation
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/configuration/limits')}>
            Open Limits & Thresholds
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/configuration/permissions-access')}>
            Open Permissions & Access
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/system-logs?tab=security')}>
            Open Security System Logs
          </SettingsRelatedLink>
          <SettingsRelatedLink onClick={() => navigate('/admin/settings/history')}>
            View Change History
          </SettingsRelatedLink>
        </div>
      </>
    ),
  }[activeId];

  return (
    <>
      <SettingsSectionShell
        subsections={SUBSECTIONS}
        activeId={activeId}
        onSelectSubsection={setActiveId}
        hasChanges={Boolean(activePanel.hasChanges)}
        saving={Boolean(activePanel.saving)}
        onSave={requestSave}
        onCancel={activePanel.cancel || (() => {})}
        showFooter={!readOnlyPanel}
      >
        {body}
      </SettingsSectionShell>

      {confirm ? (
        <SettingsConfirmModal
          title={confirm.title}
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={performSave}
          confirming={Boolean(session.saving)}
        />
      ) : null}

      <SettingsSuccessToast message={session.successMessage} />
    </>
  );
};

export default SecurityCompliancePanel;
